import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import "./Header.scss";
import ToggleSwitch from "../ToggleSwitch/ToggleSwitch";
import StyleContext from "../../contexts/StyleContext";
import {
  greeting,
  workExperiences,
  skillsSection,
  bigProjects,
  educationInfo,
  resumeSection
} from "../../portfolio";

const SECTION_IDS = [
  "greeting",
  "skills",
  "education",
  "experience",
  "projects",
  "contact"
];

function Header() {
  const {isDark} = useContext(StyleContext);
  const [active, setActive] = useState("greeting");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const viewExperience = workExperiences.display;
  const viewSkills = skillsSection.display;
  const viewProjects = bigProjects.display;
  const viewEducation = educationInfo.display;
  const viewResume = resumeSection.display;

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      const offset = window.scrollY + window.innerHeight * 0.28;
      let current = "greeting";

      SECTION_IDS.forEach(id => {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= offset) {
          current = id;
        }
      });

      setActive(current);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, {passive: true});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = e => {
      if (!headerRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    const onKeyDown = e => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, {passive: true});
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const linkClass = id => (active === id ? "nav-link is-active" : "nav-link");

  return (
    <header
      ref={headerRef}
      className={`header${isDark ? " header-dark" : " header-light"}${
        scrolled ? " header-scrolled" : ""
      }${menuOpen ? " header-menu-open" : ""}`}
    >
      <a href="/" className="logo" onClick={closeMenu}>
        <span className="grey-color">&lt;</span>
        <span className="logo-name">
          <span className="logo-name-full">{greeting.username}</span>
          <span className="logo-name-short">{greeting.title}</span>
        </span>
        <span className="grey-color">/&gt;</span>
      </a>

      <button
        type="button"
        className={`menu-icon${menuOpen ? " is-open" : ""}`}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
        aria-expanded={menuOpen}
        aria-controls="main-nav"
        onClick={() => setMenuOpen(open => !open)}
      >
        <span className={isDark ? "navicon navicon-dark" : "navicon"} />
      </button>

      {menuOpen && (
        <button
          type="button"
          className="menu-backdrop"
          aria-label="Close menu"
          onClick={closeMenu}
        />
      )}

      <ul
        id="main-nav"
        className={`menu${menuOpen ? " is-open" : ""}`}
      >
        {viewSkills && (
          <li>
            <a className={linkClass("skills")} href="#skills" onClick={closeMenu}>
              Skills
            </a>
          </li>
        )}
        {viewEducation && (
          <li>
            <a
              className={linkClass("education")}
              href="#education"
              onClick={closeMenu}
            >
              Education
            </a>
          </li>
        )}
        {viewExperience && (
          <li>
            <a
              className={linkClass("experience")}
              href="#experience"
              onClick={closeMenu}
            >
              Experience
            </a>
          </li>
        )}
        {viewProjects && (
          <li>
            <a
              className={linkClass("projects")}
              href="#projects"
              onClick={closeMenu}
            >
              Projects
            </a>
          </li>
        )}
        {viewResume && (
          <li>
            <a
              href={greeting.resumeLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
            >
              Resume
            </a>
          </li>
        )}
        <li>
          <a className={linkClass("contact")} href="#contact" onClick={closeMenu}>
            Contact
          </a>
        </li>
        <li>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a>
            <ToggleSwitch />
          </a>
        </li>
      </ul>
    </header>
  );
}
export default Header;
